import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { DISCHARGE_CALENDAR_ROUTES } from 'components/dischargeCalendar/constants';
import { Flags } from 'constants/flags';
import { useEnvironment } from 'context/environment';
import { useProfile } from 'context/profile';
import { ImportType } from 'models/Client';
import { PERMISSIONS, PRODUCT_ACCESSORS } from 'models/Profile';
import AdmissionsIcon from 'svg/AdmissionsIcon';
import AllPatientsIcon from 'svg/AllPatientsIcon';
import ArrowLeft from 'svg/ArrowLeft';
import BusinessDevelopmentIcon from 'svg/BusinessDevelopmentIcon';
import ContractedHealthPlansIcon from 'svg/ContractedHealthPlansIcon';
import CoordinatePatientsIcon from 'svg/CoordinatePatientsIcon';
import DischargeCalendarIcon from 'svg/DischargeCalendarIcon';
import GearIcon from 'svg/GearIcon';
import PatientPortfolioIcon from 'svg/PatientPortfolioIcon';
import PerformanceEngagementIcon from 'svg/PerformanceEngagementIcon';
import ReferralCoordinationIcon from 'svg/ReferralCoordinationIcon';
import ReferralsIcon from 'svg/ReferralIcon';
import RelinkIcon from 'svg/RelinkIcon';
import SummarizationIcon from 'svg/SummarizationIcon';

import { ADMIN_NAV_LINKS } from './admin/constants';
import AccessGuard from './shared/AccessGuard';
import SideNavLink from './SideNavLink';
import { SideNavSection } from './SideNavSection';

const SideNavbar = () => {
  const { profile } = useProfile();
  const { environment } = useEnvironment();
  const location = useLocation();
  const [isAdminView, setIsAdminView] = useState(location.pathname.startsWith('/admin'));
  const isAdminPageActive = location.pathname.startsWith('/admin');

  // Demo Env Setup is the only admin tool that writes demo data — server-side
  // Demo::EnvironmentGuard refuses to run the seeding worker in production.
  // Hide the nav link in production so the feature isn't discoverable there,
  // but keep it visible in every other env (staging, demo, sandbox, loadtest,
  // local dev) so the team can exercise it during QA + use it for training
  // setup. Production is identified by DATADOG_ENVIRONMENT === 'production'
  // (the only env JSON that sets it to that exact value; staging is 'staging',
  // demo/sandbox are empty, loadtest is 'loadtest', local dev is undefined).
  const isProductionEnvironment = environment?.DATADOG_ENVIRONMENT === 'production';

  const showConnectPatients = (() => {
    return (
      profile.has(PERMISSIONS.patientCreate) &&
      profile.actingClient.importType === ImportType.IMPORTED_PATIENT &&
      profile.canActAsManager &&
      (profile.isAdmin || (!profile.isPhysicianTeamUser && !profile.isAffiliateUser))
    );
  })();
  const showCoordinateRequest =
    profile.actingClient.providedProviderTypes.length > 0 && profile.has(PERMISSIONS.reviewCoordinateRequestCreate);
  const showCoordinate = !profile.isSnfCustomerUser && (showConnectPatients || showCoordinateRequest);

  const showReferrals =
    !!profile.actingClient.providedProviderTypes.length ||
    !!profile.actingClient?.clientManagedGroupTypes.some((clientGroupType) => clientGroupType.config.referral);
  const showAdmissions =
    profile.has(PRODUCT_ACCESSORS.admissionsManagementEnabled) && profile.has(PERMISSIONS.reviewAdmissionView);
  // Nav-only gate: product access plus the per-user Flipper flag (P1-1). The flag
  // is on the user (enabled for internal dev/qa users); nothing is enforced server-side.
  const showInboundReferrals =
    profile.has(PRODUCT_ACCESSORS.inboundReferralsEnabled) && profile.hasFlag(Flags.InboundReferrals);
  const showDenialList =
    profile.has(PRODUCT_ACCESSORS.inboundReferralsEnabled) &&
    profile.has(PERMISSIONS.denialListView) &&
    profile.has(PERMISSIONS.denialListItemView);
  const hasHealthPlans = profile.actingClient.providedProviderTypes.some((x) => x.config.designatesAcceptedPlans);
  const showSnfCustomerInsights = profile.isSnfCustomerUser;

  if (isAdminView) {
    return (
      <NavContainer>
        <AdminNavHeader onClick={() => setIsAdminView(false)}>
          <ArrowLeft />
          <span>Viewing: Admin</span>
        </AdminNavHeader>
        <AdminNavDivider />
        <AdminNavContent>
          {ADMIN_NAV_LINKS.map((link) => (
            <AccessGuard key={link.title} accessor={link.accessor} strictMode={link.strictMode}>
              {link.children ? (
                <SideNavLink
                  title={link.title}
                  to={link.children.map((child) => ({
                    subtitle: child.title,
                    to: child.to,
                    permission: child.accessor,
                  }))}
                />
              ) : (
                <SideNavLink to={link.to ?? ''} title={link.title} end={link.end} />
              )}
            </AccessGuard>
          ))}
          {profile.isAdmin && !isProductionEnvironment && (
            <SideNavLink to='/admin/demo-env-setup' title='Demo Env Setup' />
          )}
        </AdminNavContent>
      </NavContainer>
    );
  }

  return (
    <NavContainer>
      <NavContent>
        <AccessGuard accessor={PRODUCT_ACCESSORS.careCoordinationEnabled}>
          <SideNavSection title='Care Coordination'>
            <SideNavLink
              to='/patients'
              title='Patient Portfolio'
              data-cy='patientPortfolioLink'
              icon={<PatientPortfolioIcon width={16} height={16} />}
            />
            <SideNavLink
              to='/all-patients'
              title='All Patients'
              data-cy='allPatientsLink'
              icon={<AllPatientsIcon width={16} height={16} />}
            />
            <SideNavLink
              to='/coordinate'
              data-cy='coordinateLink'
              title='Coordinate'
              icon={<CoordinatePatientsIcon color={'currentColor'} width={16} height={16} />}
              visible={showCoordinate}
            />
          </SideNavSection>
          <SideNavSection title='Census Management' visible={profile.has(PRODUCT_ACCESSORS.dischargeCalendarEnabled)}>
            <SideNavLink
              to={DISCHARGE_CALENDAR_ROUTES.BASE}
              title='Discharge Calendar'
              data-cy='discharge-calendar'
              icon={<DischargeCalendarIcon width={16} height={16} />}
              end={false}
            />
          </SideNavSection>
          <SideNavSection title='Transitions of Care' visible={showReferrals || showAdmissions || showDenialList}>
            {showDenialList ? (
              <SideNavLink
                data-cy='admissions-link'
                title='Admissions'
                icon={<AdmissionsIcon width={16} height={16} />}
                to={[
                  { subtitle: 'Admissions', to: '/admissions', visible: showAdmissions },
                  { subtitle: 'Denial List', to: '/admissions/denial-list' },
                ]}
              />
            ) : (
              <SideNavLink
                data-cy='admissions-link'
                to='/admissions'
                title='Admissions'
                icon={<AdmissionsIcon width={16} height={16} />}
                visible={showAdmissions}
                end={false}
              />
            )}
            <SideNavLink
              data-cy='referrals-link'
              to='/referrals'
              title='Active Referrals'
              icon={<ReferralsIcon width={16} height={16} />}
              visible={showReferrals}
            />
            <SideNavLink
              data-cy='contractedHealthPlansLink'
              to='/contracted-health-plans'
              title='Contracted Health Plans'
              icon={<ContractedHealthPlansIcon width={16} height={16} />}
              visible={hasHealthPlans}
            />
          </SideNavSection>
          <SideNavSection title='Insights'>
            {showSnfCustomerInsights ? (
              <>
                <SideNavLink
                  to={[
                    { to: '/insights', subtitle: 'Facility Dashboard' },
                    { to: '/insights/post-discharge', subtitle: 'Post Discharge Dashboard' },
                  ]}
                  title='Performance & Engagement'
                  icon={<PerformanceEngagementIcon width={24} height={24} />}
                />
                <SideNavLink
                  to='/insights/referrals'
                  title='Referral Coordination'
                  icon={<ReferralCoordinationIcon width={16} height={16} />}
                />
                <SideNavLink
                  to='/insights/business-development'
                  title='Business Development'
                  icon={<BusinessDevelopmentIcon width={16} height={16} />}
                />
              </>
            ) : (
              <SideNavLink
                to='/insights'
                title='Performance & Engagement'
                icon={<PerformanceEngagementIcon width={24} height={24} />}
                end={false}
              />
            )}
          </SideNavSection>
        </AccessGuard>
        <SideNavSection title='Referrals' visible={showInboundReferrals}>
          <SideNavLink
            to='/portal-automation'
            data-testid='portalAutomationLink'
            title='Portal Automation'
            icon={<RelinkIcon size={16} />}
            end={false}
          />
          <SideNavLink
            to='/inbound-referrals'
            data-cy='inboundReferralsLink'
            title='Inbound Referrals'
            icon={<ReferralsIcon width={16} height={16} />}
            end={false}
          />
        </SideNavSection>
        <SideNavSection title='Workspaces' visible={profile.has(PRODUCT_ACCESSORS.olioSummarizationEnabled)}>
          <SideNavLink
            title='Summarization'
            data-cy='summarizationLink'
            icon={<SummarizationIcon width={16} height={16} />}
            to={[
              {
                to: '/summarizations/new',
                subtitle: 'New Summary',
              },
              {
                to: '/summarizations',
                subtitle: 'History',
              },
            ]}
          />
        </SideNavSection>
        <SideNavSection title='Admin' visible={profile.isAdmin}>
          <AdminNavButton $active={isAdminPageActive} onClick={() => setIsAdminView(true)}>
            <GearIcon width={18} height={18} />
            <span className='link-title'>Admin</span>
          </AdminNavButton>
        </SideNavSection>
      </NavContent>
    </NavContainer>
  );
};

export default SideNavbar;

const NavContainer = styled.div`
  height: 100%;
  min-width: ${({ theme }) => theme.dimensions.sideNavWidth};
  background-color: ${({ theme }) => theme.colors.black};
  flex: 0;
  display: none;
  flex-direction: column;

  @media ${({ theme }) => theme.devices.desktop} {
    display: flex;
  }
`;

const NavContent = styled.nav`
  display: flex;
  flex: 1;
  flex-direction: column;

  @media ${({ theme }) => theme.devices.desktop} {
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme.colors.white50};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }
  }
`;

const AdminNavHeader = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 16px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  font-family: 'Lato';
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18px;
  cursor: pointer;
  text-align: left;
  width: 100%;
`;

const AdminNavDivider = styled.div`
  flex-shrink: 0;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.white15};
`;

const AdminNavContent = styled.nav`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding-top: 8px;

  @media ${({ theme }) => theme.devices.desktop} {
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme.colors.white50};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }
  }
`;

const AdminNavButton = styled.button<{ $active?: boolean }>`
  border: 2px solid transparent;
  background: transparent;
  color: ${({ theme, $active }) => ($active ? theme.colors.white : theme.colors.white75)};
  font-family: 'Lato';
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 700 : 'normal')};
  line-height: 16px;
  align-items: center;
  cursor: pointer;
  display: flex;
  padding: 14px;
  width: 100%;
  text-align: left;
  ${({ $active, theme }) =>
    $active &&
    css`
      border-left-color: ${theme.colors.white};
      background-color: ${theme.colors.white15};
    `}

  & > svg,
  & > div {
    margin-right: 10px;
    opacity: ${({ $active }) => ($active ? 1 : 0.5)};
  }
`;
